import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UpdateRequiredModal } from "@/components/pwa/UpdateRequiredModal";
import { UpdateBanner } from "@/components/pwa/UpdateBanner";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { usePWA } from "@/hooks/usePWA";
import { usePageTracking } from "@/hooks/usePageTracking";
import { PageLoader } from "@/components/ui/PageLoader";

// Critical path - eager load
import HomeLanding from "./pages/HomeLanding";
import Play from "./pages/Play";
import NotFound from "./pages/NotFound";

// Secondary routes - lazy load
const Archive = lazy(() => import("./pages/Archive"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Help = lazy(() => import("./pages/Help"));
const About = lazy(() => import("./pages/About"));
const DevNotes = lazy(() => import("./pages/DevNotes"));
const Install = lazy(() => import("./pages/Install"));
const Offline = lazy(() => import("./pages/Offline"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Admin = lazy(() => import("./pages/Admin"));

// Blog posts - lazy load
const HowToWinWordPop = lazy(() => import("./pages/blog/HowToWinWordPop"));
const WordPopHiddenTips = lazy(() => import("./pages/blog/WordPopHiddenTips"));
const WhySixTries = lazy(() => import("./pages/blog/WhySixTries"));
const BehindTheScenes = lazy(() => import("./pages/blog/BehindTheScenes"));
const PsychologyOfWordGames = lazy(() => import("./pages/blog/PsychologyOfWordGames"));

const queryClient = new QueryClient();

// Page tracking component - must be inside BrowserRouter
const PageTracker = () => {
  usePageTracking();
  return null;
};

const AppContent = () => {
  const { updateRequired, isForced, dismissUpdate } = useVersionCheck();
  const { updateAvailable, performUpdate } = usePWA();

  return (
    <>
      <UpdateRequiredModal 
        open={updateRequired} 
        isForced={isForced}
        onDismiss={dismissUpdate}
        onOpenChange={(open) => { if (!open) dismissUpdate(); }}
      />
      {updateAvailable && <UpdateBanner onUpdate={performUpdate} />}
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomeLanding />} />
            <Route path="/play" element={<Play />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/puzzle/:date" element={<Play />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<About />} />
            <Route path="/dev-notes" element={<DevNotes />} />
            <Route path="/install" element={<Install />} />
            <Route path="/offline" element={<Offline />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/blog/how-to-win-wordpop" element={<HowToWinWordPop />} />
            <Route path="/blog/wordpop-hidden-tips" element={<WordPopHiddenTips />} />
            <Route path="/blog/why-wordpop-6-tries" element={<WhySixTries />} />
            <Route path="/blog/wordpop-behind-the-scenes" element={<BehindTheScenes />} />
            <Route path="/blog/psychology-of-word-games" element={<PsychologyOfWordGames />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;